import api from '../config/axios'; // Assumendo che l'istanza axios configurata sia esportata come 'api'

/**
 * Oggetto API per interagire con gli endpoint del generatore.
 */
export const generatorApi = {
  /**
   * Recupera le risorse iniziali per il generatore (scheletri, prodotti, tutti i moduli).
   * @returns {Promise<Object>} Una promessa che risolve con i dati delle risorse.
   *                            Es: { skeletons: [], products: [], modules: [] }
   */
  fetchResources: () => api.get('/generator/resources').then(response => response.data),

  /**
   * Recupera gli scheletri per una specifica root directory.
   * @param {string} skeletonRoot - La directory radice degli scheletri selezionata.
   * @returns {Promise<Array<Object>>} Una promessa che risolve con un array di scheletri.
   */
  fetchSkeletonsByRoot: (skeletonRoot) => api.get(`/generator/skeletons/${encodeURIComponent(skeletonRoot)}`).then(response => response.data),

  /**
   * Recupera i moduli specifici per un dato ID prodotto.
   * @param {string|number} productId - L'ID del prodotto per cui recuperare i moduli.
   * @returns {Promise<Array<Object>>} Una promessa che risolve con un array di moduli.
   */
  fetchModules: (productId) => api.get(`/generator/modules/${productId}`).then(response => response.data),

  /**
   * Avvia il processo di generazione con i parametri forniti.
   * @param {Object} body - Il payload della richiesta contenente i parametri di generazione.
   *                        Es: { scheletro: 'nome.skl', chiavi: 1, jsonPayload: '{}', ... }
   * @returns {Promise<Object>} Una promessa che risolve con le informazioni sull'avvio del job.
   *                            Es: { jobId: 'gen_12345', message: 'Processo avviato.' }
   */
  runGeneration: (body) => {
    // Assicurati che selectedSkeletonRoot sia inclusa nel body se non già presente
    // dal componente chiamante. Il body dovrebbe contenere { scheletro, selectedSkeletonRoot, ... }
    return api.post('/generator/run', body).then(response => response.data);
  },
  
  /**
   * Recupera il contenuto del log per un job di generazione specifico.
   * @param {string} jobId - L'identificativo del job di generazione.
   * @param {string} [logFilePath=null] - Il percorso del file di log da recuperare (opzionale).
   * @returns {Promise<string>} Una promessa che risolve con il contenuto testuale del file di log.
   */
  fetchGenerationLog: (jobId, logFilePath = null) => {
    const url = `/generator/logs/${jobId}`;
    const params = logFilePath ? { logFilePath } : {};
    
    return api.get(url, {
      params,
      transformResponse: [(data) => data],
      headers: {
        'Accept': 'text/plain'
      }
    }).then(response => response.data);
  },

  /**
   * Recupera gli item di contesto basati sullo scheletro selezionato, prodotto e moduli.
   * @param {string} skeletonPath - Path del file .skl selezionato.
   * @param {string|number} productId - ID del prodotto selezionato (può essere vuoto o null).
   * @param {Array<string|number>} selectedModuleIds - Array degli ID dei moduli selezionati.
   * @returns {Promise<Object>} Una promessa che risolve con i dati di contesto.
   *                            Es: { guideTableNameForLabel: "...", guideTableItems: [], ... }
   */
  fetchSkeletonContextItems: (selectedSkeletonRoot, skeletonPath, productId, selectedModuleIds) => {
    const params = new URLSearchParams();
    if (selectedSkeletonRoot) { // AGGIUNTO
      params.append('selectedSkeletonRoot', selectedSkeletonRoot);
    }
    if (skeletonPath) {
      params.append('skeletonPath', skeletonPath);
    }
    // Ensure productId is only appended if it has a meaningful value
    if (productId !== undefined && productId !== null && productId !== '') {
      params.append('productId', productId);
    }
    if (selectedModuleIds && selectedModuleIds.length > 0) {
      selectedModuleIds.forEach(id => params.append('selectedModuleIds[]', id));
    }
    return api.get(`/generator/skeleton-context?${params.toString()}`).then(response => response.data);
  },

  /**
   * Scarica un file ZIP contenente i file generati.
   * @param {Array<Object>} filesToDownload - Array di oggetti contenenti 'filePath' e 'fileName'.
   * @returns {Promise<void>} Una promessa che risolve quando il download è completato.
   */
  downloadZip: (filesToDownload) => {
    return api.post('/generator/download-zip', { files: filesToDownload }, {
      responseType: 'blob',
    }).then(response => {
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // SIMPLE: Generate filename based on content or timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').substring(0, 19);
      const baseFileName = filesToDownload.length === 1 
        ? filesToDownload[0].fileName.replace(/\.[^/.]+$/, '') // Remove extension
        : 'generated_files';
      
      const fileName = `${baseFileName}_${timestamp}.zip`;
      
      // Filename generated successfully
      link.setAttribute('download', fileName);
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }).catch(error => {
      console.error('Error downloading ZIP:', error);
      throw error;
    });
  },

  /**
   * Recupera il contenuto testuale di un file generato per la preview inline.
   * @param {string} filePath - Il percorso assoluto del file da visualizzare.
   * @returns {Promise<Object>} Una promessa che risolve con { fileName, content } o { fileName, content: null, message }.
   */
  previewFile: (filePath) => {
    return api.get('/generator/preview-file', { params: { filePath } }).then(response => response.data);
  },
};

// Non è necessario un export default se si esporta solo generatorApi in questo modo.
// Se si preferisce un export default, si può fare:
// const generatorService = { fetchResources, fetchModules, runGeneration, fetchGenerationLog };
// export default generatorService;